import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Home, Video, Target, BookOpen, User, FileText, Bell, HelpCircle,
} from "lucide-react";
import OfflineBanner from "../components/shared/OfflineBanner";
import ResumeSessionModal from "../components/interview/ResumeSessionModal";
import useTenantBranding from "../hooks/useTenantBranding";

// Brand fallback when a tenant supplies no primary colour — V2 violet.
const BRAND_FALLBACK = "#7B5AC4";

// ── CandidateLayout ────────────────────────────────────────────────────────
// Branded per tenant (logo + primary color from useTenantBranding) and
// responsive: vertical sidebar on >=md screens, bottom tab bar on <md.
// Sticky offline banner + crash-resume modal are global to the candidate
// experience.

const NAV_ITEMS = [
  { path: "/candidate/dashboard",     icon: Home,       label: "Home" },
  { path: "/candidate/interviews",    icon: Video,      label: "Interviews" },
  { path: "/candidate/practice",      icon: Target,     label: "Practice" },
  { path: "/candidate/resources",     icon: BookOpen,   label: "Resources" },
  { path: "/candidate/profile",       icon: User,       label: "Profile" },
  { path: "/candidate/documents",     icon: FileText,   label: "Documents" },
  { path: "/candidate/notifications", icon: Bell,       label: "Notifications" },
  { path: "/candidate/help",          icon: HelpCircle, label: "Help" },
];

// Bottom-bar shows only the most-used 5 to fit thumb-reach. Help moves
// into the More menu (currently rendered in the sidebar at md+; on
// mobile users reach Help via the profile page footer or by typing
// the URL).
const BOTTOM_BAR_ITEMS = NAV_ITEMS.slice(0, 5);

export default function CandidateLayout() {
  const navigate = useNavigate();
  const branding = useTenantBranding();

  const handleContinue = (session) => {
    navigate(`/candidate/interview/${session.interviewId}?resume=${session.id}`);
  };
  const handleRestart = (session) => {
    navigate(`/candidate/interview/${session.interviewId}?fresh=1&abandoned=${session.id}`);
  };

  const signOut = () => {
    localStorage.clear();
    navigate("/signin");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-vf-bg">
      <OfflineBanner />
      <ResumeSessionModal onContinue={handleContinue} onRestart={handleRestart} />

      {/* ── Sidebar (md+) ───────────────────────────────────────────── */}
      <aside
        aria-label="Primary navigation"
        className="hidden md:flex md:w-60 bg-white border-r border-subtle flex-col"
      >
        <BrandHeader branding={branding} />
        <nav className="flex-1 p-3 space-y-1">
          {NAV_ITEMS.map((item) => (
            <SidebarLink key={item.path} item={item} primaryColor={branding.primaryColor} />
          ))}
        </nav>
        <div className="p-3 border-t border-subtle">
          <button
            type="button"
            onClick={signOut}
            className="w-full text-left px-3 py-2 text-sm text-vf-muted hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Mobile top bar (<md) ───────────────────────────────────── */}
      <header className="md:hidden bg-white border-b border-subtle px-4 py-3 flex items-center gap-3 sticky top-0 z-sticky">
        <BrandBadge branding={branding} small />
        <span className="text-sm font-semibold text-vf-base truncate">{branding.name}</span>
        <button
          type="button"
          onClick={signOut}
          className="ml-auto text-2xs text-vf-muted hover:text-red-600 px-2 py-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
        >
          Sign out
        </button>
      </header>

      {/* ── Main content ───────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <div className="px-4 py-4 md:px-6 md:py-6">
          <Outlet />
        </div>
      </main>

      {/* ── Bottom tab bar (<md) ───────────────────────────────────── */}
      <nav
        aria-label="Primary navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-subtle flex items-stretch z-sticky"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {BOTTOM_BAR_ITEMS.map((item) => (
          <BottomBarLink key={item.path} item={item} primaryColor={branding.primaryColor} />
        ))}
      </nav>
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────────────

function BrandHeader({ branding }) {
  return (
    <div className="p-4 border-b border-subtle flex items-center gap-2.5">
      <BrandBadge branding={branding} />
      <span className="font-semibold text-vf-base text-sm truncate">{branding.name}</span>
    </div>
  );
}

function BrandBadge({ branding, small = false }) {
  const dim = small ? "w-7 h-7" : "w-8 h-8";
  const initial = (branding.name || "?").trim().charAt(0).toUpperCase() || "A";
  if (branding.logoUrl) {
    return (
      <img
        src={branding.logoUrl}
        alt={`${branding.name} logo`}
        className={`${dim} rounded-lg object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div
      className={`${dim} rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
      style={{ background: branding.primaryColor || BRAND_FALLBACK }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

function SidebarLink({ item, primaryColor }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary ${
          isActive ? "font-semibold" : "text-vf-muted hover:bg-vf-tint"
        }`
      }
      style={({ isActive }) =>
        isActive
          ? { background: `${primaryColor || BRAND_FALLBACK}15`, color: primaryColor || BRAND_FALLBACK }
          : undefined
      }
    >
      <Icon size={18} style={{ flexShrink: 0 }} aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}

function BottomBarLink({ item, primaryColor }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        `flex-1 flex flex-col items-center justify-center py-2 text-2xs gap-0.5 transition-colors min-h-[48px] ${
          isActive ? "font-semibold" : "text-vf-muted"
        }`
      }
      style={({ isActive }) =>
        isActive ? { color: primaryColor || BRAND_FALLBACK } : undefined
      }
    >
      <Icon size={20} aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}
